import {
  FC,
  Fragment,
  ReactElement,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import DP, { ReactDatePickerCustomHeaderProps, ReactDatePickerProps } from 'react-datepicker';

import { offset, size, flip } from '@floating-ui/react-dom';
import { format, getYear, getMonth, isSameDay } from 'date-fns';
import { is, enUS as en } from 'date-fns/locale';
import styled from 'styled-components';

import 'react-datepicker/dist/react-datepicker.css';
import { FixedFullscreenOverlay } from '@components/fixed-fullscreen-overlay';
import { FormError } from '@components/form-error';
import { FieldDescription, FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDate } from '@components/icons/date';
import { IconDropdown } from '@components/icons/dropdown';
import { IconFormIndicatorLocked } from '@components/icons/form-indicator-locked';
import { MaskedInput } from '@components/masked-input';
import createAutoCorrectedDatePipe from '@components/masked-input/pipe/create-auto-corrected-date-pipe';
import { Menu } from '@components/menu';
import {
  handlePopoverSizeChange,
  moveUpMiddleware,
} from '@components/popover-button/popover-button';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';
import { theme } from '@styles/theme';
import { isMobileDevice, noop } from '@utils/browser';
import { parseDate } from '@utils/date';
import { transitionStandard } from '@utils/transitions';

export const DATEPICKER_PORTAL_ID = 'datepicker-root';

const locales = {
  is,
  en,
};

export type LanguageOptions = 'en' | 'is';

export type DatePickerProps = Omit<
  ReactDatePickerProps,
  'value' | 'onChange' | 'placeholderText' | 'disabled'
> & {
  onClear?: () => void;
  lang?: LanguageOptions;
  error?: string;
  label?: string;
  labelIcon?: ReactElement<IconProps>;
  defaultValue?: string | Date;
  value?: string | Date;
  placeholder?: string;
  isDisabled?: boolean;
  onChange?(date: Date): void;
  hideLabel?: boolean;
  customInput?: React.ReactNode;
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
  description?: string;
};

const thisYear = getYear(new Date());
const inputMask = [/\d/, /\d/, ' ', '/', ' ', /\d/, /\d/, ' ', '/', ' ', /\d/, /\d/, /\d/, /\d/];

export const DatePicker = (props: DatePickerProps) => {
  const {
    value,
    onChange,
    onClear,
    dateFormat = 'dd / MM / yyyy',
    lang = 'en',
    error = '',
    defaultValue,
    inline,
    label,
    labelIcon,
    isDisabled,
    placeholder,
    hideLabel,
    customInput,
    disablePastDates,
    disableFutureDates,
    minDate: minDateProp,
    maxDate: maxDateProp,
    description,
    ...rest
  } = props;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const minDate = disablePastDates ? minDateProp ?? today : minDateProp;
  const maxDate = disableFutureDates ? maxDateProp ?? today : maxDateProp;
  const [localValue, setLocalValue] = useState(defaultValue ? parseDate(defaultValue) : undefined);
  const [viewingDate, setViewingDate] = useState(value ? parseDate(value) : new Date());
  const [open, setOpen] = useState(false);
  const parsedValue = value ? parseDate(value) : localValue;
  const selected = parsedValue && isNaN(parsedValue.getTime()) ? undefined : parsedValue;
  const autoCorrectionPipe = createAutoCorrectedDatePipe('dd / mm / yyyy', {
    minYear: getYear(new Date()) - 100,
    maxYear: getYear(new Date()) + 100,
  });
  const LabelWrapper = hideLabel ? VisuallyHidden : Fragment;
  const handleChange = (date: Date) => {
    setLocalValue(date);
    onChange?.(date);
  };

  return (
    <OverlayContext.Provider value={{ isOpen: open, onClick: () => setOpen(false) }}>
      <Stack space={1}>
        <LabelWrapper>
          <FormLabel text={label} icon={labelIcon} />
        </LabelWrapper>
        <FieldDescription text={description} />
        <RelativeWrapper>
          <StyledDatePicker
            calendarClassName={inline ? 'calendar-inline' : ''}
            portalId={inline ? undefined : DATEPICKER_PORTAL_ID}
            renderCustomHeader={(props) => (
              <DatePickerHeader {...props} lang={lang} onViewDateChange={setViewingDate} />
            )}
            popperContainer={CustomPopperContainer}
            customInput={
              isMobileDevice && open ? (
                <input inputMode="none" style={{ outline: 'none' }} />
              ) : (
                customInput ?? <MaskedInput pipe={autoCorrectionPipe} mask={inputMask} />
              )
            }
            fixedHeight
            disabled={isDisabled}
            $open={open}
            onCalendarOpen={() => setOpen(true)}
            onCalendarClose={() => setOpen(false)}
            selected={selected}
            onChange={handleChange}
            showPopperArrow={false}
            withPortal={isMobileDevice}
            popperPlacement="bottom-start"
            popperModifiers={[
              offset(-10),
              flip(),
              moveUpMiddleware,
              size({
                apply: handlePopoverSizeChange,
              }),
            ]}
            placeholderText={placeholder}
            dateFormat={dateFormat}
            locale={locales[lang]}
            $error={error}
            inline={inline}
            minDate={minDate}
            maxDate={maxDate}
            dayClassName={(date: Date) => {
              const currentMonth = getMonth(viewingDate);
              const dateMonth = getMonth(date);
              if (currentMonth !== dateMonth) {
                return 'not-current-month';
              } else {
                const properValue = value ? parseDate(value) : undefined;
                if (properValue && isSameDay(date, properValue)) return 'is-selected';
              }

              return '';
            }}
            renderDayContents={(day: number) => (
              <span data-cy={`datepicker-day-${day}`}>{day}</span>
            )}
            {...rest}
          />
          {!inline &&
            (isDisabled ? (
              <IconContainer>
                <IconFormIndicatorLocked />
              </IconContainer>
            ) : (
              <IconContainer>
                <IconDate />
              </IconContainer>
            ))}
          {!inline && !isDisabled && selected && onClear && (
            <IconContainer
              onClick={() => {
                onClear();
                setLocalValue(undefined);
              }}
            >
              <IconCloseRegular />
            </IconContainer>
          )}
        </RelativeWrapper>
        <FormError text={error} />
      </Stack>
    </OverlayContext.Provider>
  );
};

type DatePickerHeaderProps = ReactDatePickerCustomHeaderProps & {
  lang: 'en' | 'is';
  onViewDateChange: (date: Date) => void;
};

const DatePickerHeader = (props: DatePickerHeaderProps) => {
  const { lang, date, changeYear, changeMonth, onViewDateChange } = props;
  useEffect(() => {
    onViewDateChange(date);
  }, [onViewDateChange, date]);

  const months = useMemo(
    function getMonths() {
      const months = [];
      const dateToSet = new Date(1977, 0, 1);
      for (let i = 0; i < 12; i += 1) {
        dateToSet.setMonth(i);
        const label = format(dateToSet, 'MMMM', { locale: locales[lang] });
        const option = { label: label, value: i };
        months.push(option);
      }
      return months;
    },
    [lang]
  );

  const years = useMemo(function getYears() {
    const years = [];
    for (let k = thisYear - 100; k < thisYear + 100; k += 1) {
      const option = { label: k, value: k };
      years.push(option);
    }
    return years;
  }, []);

  return (
    <Stack space={1} justify="space-between" orientation="horizontal">
      <Menu
        skipPortalWrapper
        buttonProps={{
          text: format(date, 'MMMM', { locale: locales[lang] }),
          variant: 'ghost',
          iconRight: <IconDropdown />,
        }}
        maxHeight={300}
        placement="bottom-start"
      >
        {months.map((month) => {
          const isActive = month.value === date.getMonth();
          return (
            <Menu.Item
              isActive={isActive}
              key={month.value}
              onSelect={() => {
                changeMonth(month.value);
              }}
              label={month.label}
              variant="base"
            />
          );
        })}
      </Menu>
      <Menu
        skipPortalWrapper
        buttonProps={{
          text: String(date.getFullYear()),
          variant: 'ghost',
          iconRight: <IconDropdown />,
        }}
        maxHeight={300}
        placement="bottom-end"
      >
        {years.map((year) => {
          const isActive = year.value === date.getFullYear();
          return (
            <Menu.Item
              isActive={isActive}
              key={year.value}
              onSelect={() => {
                changeYear(year.value);
              }}
              label={year.label}
              variant="base"
            />
          );
        })}
      </Menu>
    </Stack>
  );
};

const OverlayContext = createContext<{ onClick(): void; isOpen: boolean }>({
  isOpen: false,
  onClick: noop,
});

const CustomPopperContainer: FC = ({ children }) => {
  const { isOpen, onClick } = useContext(OverlayContext);
  return (
    <>
      {isOpen && <FixedFullscreenOverlay onClick={onClick} />}
      {children}
    </>
  );
};

const RelativeWrapper = styled.div({
  position: 'relative',
});

const IconContainer = styled.div({
  position: 'absolute',
  right: '0.5rem',
  top: 'calc(50% - 0.75rem)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.form.icon.color,
  ':hover, :focus': {
    cursor: 'pointer',
    color: theme.form.icon.colorFocus,
  },
});

const StyledDatePicker = styled(DP)<{ $error: boolean | string; $open: boolean }>((p) => ({
  height: theme.base.height,
  width: '100%',
  borderRadius: theme.form.border.radius,
  backgroundColor: theme.form.bg.color,
  font: theme.font.regular16,
  borderWidth: theme.form.border.width,
  borderStyle: 'solid',
  borderColor: theme.form.border.color,
  color: theme.form.value.color,
  padding: '0.5rem 0.75rem',
  transition: transitionStandard('all'),
  ...(!p.$open && {
    '&:not(:disabled)': {
      ':hover': {
        backgroundColor: theme.form.bg.colorHover,
      },
    },
    '::placeholder': {
      font: theme.font.regular14,
      color: theme.form.placeholder.color,
    },
  }),
  ...(p.$error && {
    borderColor: theme.color.error,
  }),
  ...(p.$open && {
    outline: 'none',
    color: theme.form.value.colorFocus,
    backgroundColor: theme.form.bg.colorFocus,
    borderColor: theme.form.border.colorFocus,
    '::placeholder': {
      font: theme.font.regular14,
      color: theme.form.placeholder.colorFocus,
    },
  }),
}));
